import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  RefreshCw, Sparkles, PenLine, Check, X, Clock, Plus, Link, Minus, ExternalLink, Share2, Copy,
  Linkedin, Twitter, Facebook, CalendarDays,
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { BACKEND_URL } from "../../lib/backend";
import { AutopilotSelectionPanel } from "./AutopilotSelectionPanel";

export default function BriefTab({
  dm,
  brief, briefContent, setBriefContent,
  briefPhotoUrl, setBriefPhotoUrl, briefPublished, setBriefPublished,
  generatingBrief, savingBrief, loadingBrief,
  generateBrief, saveBrief, saveDraft, uploadBriefPhoto, photoInputRef, uploadingPhoto,
  editorComment, setEditorComment, editorUrls, setEditorUrls,
  newEditorUrl, setNewEditorUrl, editorNotesActive,
  savingEditorNotes, loadingEditorNotes, saveEditorNotes, clearEditorNotes, addEditorUrl,
  addingEditorUrl = false,
  autoSettings, setAutoSettings, savingAutoSettings, saveAutoSettings,
  briefOgPrompt, setBriefOgPrompt, regeneratingBriefOg, regenerateBriefOg,
  socialTexts, setSocialTexts,
  briefFeatured = [], loadingBriefFeatured = false,
  removeBriefFeatured, clearBriefFeatured,
  authHeader,
}) {
  // Auto-compute social texts from brief content (mirrors backend logic)
  const computed = useMemo(() => {
    if (!briefContent) return { linkedin: "", twitter: "", facebook: "" };
    const title = briefContent.match(/^#\s+(.+)$/m)?.[1]?.trim() || "Travel Intelligence Brief";
    const slug = brief?.seo_slug || brief?.slug || "";
    const url = slug ? `${BACKEND_URL}/api/preview/brief/${slug}` : BACKEND_URL;
    const preview = briefContent.split("\n").find(l => {
      const s = l.trim();
      return s && !s.startsWith("#") && !s.startsWith("---") && !s.startsWith("*") && s.length > 40;
    })?.slice(0, 300) || "";
    return {
      linkedin: `📰 ${title}\n\n${preview}\n\n→ ${url}\n\n#TourismIntel #TravelIndustry #TravelTech`,
      twitter:  `${title.slice(0, 160)} → ${url} #TourismIntel`.slice(0, 280),
      facebook: `${title}\n\n${preview}\n\n${url}`,
    };
  }, [briefContent, brief]);

  const getSocialText = (platform) =>
    (socialTexts[platform] !== undefined && socialTexts[platform] !== null)
      ? socialTexts[platform]
      : computed[platform];

  const setSocialText = (platform, val) =>
    setSocialTexts(prev => ({ ...prev, [platform]: val }));

  const resetSocialTexts = () => setSocialTexts({});

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
  };

  // "News in this brief" backfill (iter141): fills source_articles on archive
  // briefs from the matching autopilot run — no LLM involved.
  const [backfillingSources, setBackfillingSources] = useState(false);
  const runSourcesBackfill = async () => {
    setBackfillingSources(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/briefs/backfill-sources`, null, {
        headers: authHeader(), timeout: 120000,
      });
      toast.success(`News sources: ${res.data.filled} filled · ${res.data.skipped_no_run} skipped (no autopilot run)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sources backfill failed");
    } finally {
      setBackfillingSources(false);
    }
  };
  return (
    <TabsContent value="brief">
      <div className="max-w-3xl w-full">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <h2 className={`editorial-heading text-xl sm:text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Brief of the Week</h2>
            <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Your personal strategic view on travel & tourism — AI-generated from the last 7 days, editable. Shown on the public homepage.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={runSourcesBackfill}
              disabled={backfillingSources}
              data-testid="backfill-sources-btn"
              title='Fill the "News in this brief" list on past briefs from their autopilot run'
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full category-tag text-sm transition-all disabled:opacity-50 ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}
            >
              {backfillingSources ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link className="w-3.5 h-3.5" />}
              Backfill sources
            </button>
            {briefContent && !brief?.is_published && (
              <button
                onClick={saveDraft}
                disabled={savingBrief || !briefContent.trim()}
                data-testid="save-draft-btn"
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full category-tag text-sm transition-all disabled:opacity-50 ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}
              >
                {savingBrief ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save Draft
              </button>
            )}
            <button
              onClick={generateBrief}
              disabled={generatingBrief}
              data-testid="generate-brief-btn"
              className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-full category-tag transition-all disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
            >
              {generatingBrief ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Generating…</> : <><Sparkles className="w-3.5 h-3.5" />Generate with AI</>}
            </button>
          </div>
        </div>

        {loadingBrief ? (
          <div className="flex justify-center py-16"><RefreshCw className={`w-5 h-5 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>
        ) : (
          <div className="space-y-5">
            {/* This week's autopilot news selection (view + remove single articles) */}
            <AutopilotSelectionPanel dm={dm} authHeader={authHeader} />

            {/* Editor's Input Panel */}
            <div className={`rounded-2xl border-2 p-6 ${editorNotesActive ? (dm ? "border-cyan-400/40 bg-cyan-400/5" : "border-[#006874]/30 bg-[#006874]/5") : (dm ? "border-[#2a3435] bg-[#1a2122]" : "border-[#e8ecee] bg-white")}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.04), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`editorial-heading text-base font-medium flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                    <PenLine className="w-4 h-4" />
                    Editor&apos;s Input
                    {editorNotesActive && (
                      <span className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-cyan-400/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]"}`}>
                        Active — will be used in next generation
                      </span>
                    )}
                  </h3>
                  <p className={`category-tag mt-0.5 text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                    Add editorial guidance or URLs to personalise the next brief. One-shot — cleared automatically after generation.
                  </p>
                </div>
                {editorNotesActive && (
                  <button
                    onClick={clearEditorNotes}
                    data-testid="clear-editor-notes-btn"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full category-tag text-xs transition-all ${dm ? "text-red-400 hover:bg-red-400/10" : "text-red-500 hover:bg-red-50"}`}
                  >
                    <X className="w-3 h-3" />Clear
                  </button>
                )}
              </div>

              <div className="mb-4">
                <Label className={`category-tag block mb-2 text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                  Editorial Guidance <span className={`${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>(tone, angle, perspective — AI reads this before writing)</span>
                </Label>
                <textarea
                  data-testid="editor-comment-input"
                  value={editorComment}
                  onChange={e => setEditorComment(e.target.value)}
                  rows={3}
                  placeholder="e.g. This week I want to focus on AI adoption in hotel operations…"
                  className={`w-full resize-none rounded-xl px-4 py-3 text-sm leading-relaxed border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder-[#bac9cc]"}`}
                />
              </div>

              <div className="mb-4">
                <Label className={`category-tag block mb-2 text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                  Force-Include URLs <span className={`${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>(scraped, summarised, and added to article pool)</span>
                </Label>
                <div className="flex gap-2">
                  <input
                    data-testid="editor-url-input"
                    value={newEditorUrl}
                    onChange={e => setNewEditorUrl(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addEditorUrl()}
                    placeholder="https://example.com/article"
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder-[#bac9cc]"}`}
                  />
                  <button
                    onClick={addEditorUrl}
                    data-testid="add-editor-url-btn"
                    disabled={addingEditorUrl || !newEditorUrl.trim()}
                    className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full category-tag text-sm transition-all disabled:opacity-50 ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}
                  >
                    {addingEditorUrl ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Scraping…</> : <><Plus className="w-3.5 h-3.5" />Add</>}
                  </button>
                </div>
                {editorUrls.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {editorUrls.map((url, i) => (
                      <div key={url || i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                        <Link className={`w-3 h-3 flex-shrink-0 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                        <span className={`flex-1 truncate ${dm ? "text-slate-400" : "text-[#3b494c]"}`}>{url}</span>
                        <button
                          onClick={() => setEditorUrls(prev => prev.filter((_, j) => j !== i))}
                          className={`${dm ? "text-slate-600 hover:text-red-400" : "text-[#bac9cc] hover:text-red-500"} transition-colors`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={saveEditorNotes}
                disabled={savingEditorNotes || loadingEditorNotes || (!editorComment.trim() && editorUrls.length === 0)}
                data-testid="save-editor-notes-btn"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full category-tag text-sm transition-all disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
              >
                {savingEditorNotes ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Saving…</> : <><Check className="w-3.5 h-3.5" />Save Editor&apos;s Input</>}
              </button>
            </div>

            {/* Selected articles for next brief (editable queue — mirror of podcast) */}
            <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }} data-testid="brief-selected-articles-card">
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className={`editorial-heading text-base font-medium flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                    Selected articles for next brief
                    {briefFeatured.length > 0 && (
                      <span className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-cyan-400/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]"}`}>
                        {briefFeatured.length} queued
                      </span>
                    )}
                  </h3>
                  <p className={`category-tag mt-0.5 text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                    Articles staged here are prioritized in the next brief. Sourced from feed (⭐ Featured), Top Performers, or force-include URLs. One-shot — cleared after generation.
                  </p>
                </div>
                {briefFeatured.length > 0 && (
                  <button
                    onClick={clearBriefFeatured}
                    data-testid="clear-brief-featured-btn"
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full category-tag text-xs transition-all flex-shrink-0 ${dm ? "text-red-400 hover:bg-red-400/10" : "text-red-500 hover:bg-red-50"}`}
                  >
                    <X className="w-3 h-3" />Clear all
                  </button>
                )}
              </div>
              {loadingBriefFeatured ? (
                <div className="flex justify-center py-4">
                  <RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                </div>
              ) : briefFeatured.length === 0 ? (
                <div className={`text-center py-6 text-sm ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                  No articles queued. Star articles in the feed, or use &quot;Use top 3 for next brief&quot; from Analytics.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {briefFeatured.map((a, i) => (
                    <div key={a.id || a.originalUrl || i} data-testid={`brief-featured-row-${i}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                      <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"}`}>{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <a
                          href={a.originalUrl || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-sm font-medium truncate block hover:underline ${dm ? "text-slate-200" : "text-[#191c1d]"}`}
                        >{a.title || "(untitled)"}</a>
                        <p className={`text-[11px] truncate ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{a.source || "Unknown source"}</p>
                      </div>
                      <button
                        onClick={() => removeBriefFeatured(a.id || a.originalUrl)}
                        data-testid={`remove-brief-featured-${i}`}
                        aria-label="Remove from queue"
                        className={`p-1 rounded-md flex-shrink-0 transition-colors ${dm ? "text-slate-500 hover:text-red-400" : "text-[#6b7a7d] hover:text-red-500"}`}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Brief text editor */}
            <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <Label className={`category-tag block mb-3 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Brief Text</Label>
              <textarea
                data-testid="brief-content-input"
                value={briefContent}
                onChange={e => setBriefContent(e.target.value)}
                rows={10}
                placeholder="Click 'Generate with AI' to create your brief, or write it manually here…"
                className={`w-full resize-y rounded-xl px-4 py-3 text-sm leading-relaxed border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder-[#bac9cc]"}`}
                style={{ fontFamily: "'Newsreader', serif", fontSize: 15, lineHeight: 1.75 }}
              />
              <p className={`mt-2 category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{briefContent.split(/\s+/).filter(Boolean).length} words</p>
            </div>

            {/* Author photo */}
            <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <Label className={`category-tag block mb-4 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Author Photo</Label>
              <div className="flex items-center gap-4">
                {briefPhotoUrl ? (
                  <img src={briefPhotoUrl} alt="Mirko Lalli" data-testid="brief-photo-preview"
                    className="w-16 h-16 rounded-full object-cover flex-shrink-0 ring-2"
                    style={{ ringColor: dm ? "#22d3ee" : "#006874" }}
                    onError={e => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className={`w-16 h-16 rounded-full flex-shrink-0 flex items-center justify-center font-black text-lg ${dm ? "bg-cyan-400/15 text-cyan-400" : "bg-[#006874]/10 text-[#006874]"}`}
                    style={{ fontFamily: "Newsreader, serif" }} data-testid="brief-photo-monogram">ML</div>
                )}
                <div className="flex-1 space-y-2.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden" data-testid="brief-photo-file-input" onChange={uploadBriefPhoto} />
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      data-testid="brief-photo-upload-btn"
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all disabled:opacity-50 ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}
                    >
                      {uploadingPhoto
                        ? <><RefreshCw className="w-3 h-3 animate-spin" />Uploading…</>
                        : <><Plus className="w-3 h-3" />{briefPhotoUrl ? "Replace Photo" : "Upload Photo"}</>}
                    </button>
                    {briefPhotoUrl && (
                      <button onClick={() => setBriefPhotoUrl("")} data-testid="brief-photo-remove-btn"
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all ${dm ? "text-red-400 hover:bg-red-400/10" : "text-red-500 hover:bg-red-50"}`}>
                        <X className="w-3 h-3" />Remove
                      </button>
                    )}
                  </div>
                  <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>JPG, PNG, WebP · auto-compressed to 400px · Or paste a URL below</p>
                  <div className="flex gap-2 mt-1">
                    <input data-testid="brief-photo-url-input" type="url" placeholder="Or paste photo URL (https://…)"
                      className={`flex-1 rounded-xl px-3 py-2 text-xs border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-300 placeholder-slate-600" : "bg-[#f3f4f5] text-[#3b494c] placeholder-[#bac9cc]"}`}
                      onBlur={e => { if (e.target.value.startsWith("http")) setBriefPhotoUrl(e.target.value.trim()); }}
                      onKeyDown={e => { if (e.key === "Enter" && e.target.value.startsWith("http")) setBriefPhotoUrl(e.target.value.trim()); }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Publish toggle + save */}
            <div className={`rounded-2xl p-6 flex items-center justify-between ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <div className="flex items-center gap-3">
                <Switch checked={briefPublished} onCheckedChange={setBriefPublished} data-testid="brief-publish-toggle" className="data-[state=checked]:bg-[#006874]" />
                <div>
                  <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{briefPublished ? "Published" : "Draft"}</p>
                  <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{briefPublished ? "Visible on the homepage" : "Hidden from public"}</p>
                </div>
              </div>
              <button onClick={saveBrief} disabled={savingBrief || !briefContent.trim()} data-testid="save-brief-btn"
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full category-tag transition-all disabled:opacity-50 ${dm ? "bg-[#252e2f] text-slate-200 hover:bg-[#303b3c]" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
                {savingBrief ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Saving…</> : <><Check className="w-3.5 h-3.5" />Save Brief</>}
              </button>
            </div>

            {/* OG Image Settings */}
            <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`editorial-heading text-base font-medium flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                    <Share2 className="w-4 h-4" />Social Preview (OG Image)
                  </h3>
                  <p className={`category-tag mt-0.5 text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                    Auto-generata via Nano Banana per la condivisione social. Personalizza lo stile con istruzioni aggiuntive.
                  </p>
                </div>
                {(brief?.seo_slug || brief?.slug) && (
                  <a
                    href={`${BACKEND_URL}/api/og/brief/${brief.seo_slug || brief.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="preview-brief-og-btn"
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full category-tag text-xs transition-all ${dm ? "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]"}`}
                  >
                    <ExternalLink className="w-3 h-3" />Preview
                  </a>
                )}
              </div>
              <div className="mb-4">
                <Label className={`category-tag block mb-2 text-xs ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                  Istruzioni stile personalizzate{" "}
                  <span className={`${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>(colori, mood, elementi grafici — si aggiungono al prompt base)</span>
                </Label>
                <textarea
                  data-testid="brief-og-prompt-input"
                  value={briefOgPrompt || ""}
                  onChange={e => setBriefOgPrompt(e.target.value)}
                  rows={3}
                  placeholder="e.g. Use a dark navy background with gold accents. Add a subtle wave pattern. Premium, editorial style."
                  className={`w-full resize-none rounded-xl px-4 py-3 text-sm leading-relaxed border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder-[#bac9cc]"}`}
                />
              </div>
              <button
                onClick={regenerateBriefOg}
                disabled={regeneratingBriefOg}
                data-testid="regenerate-brief-og-btn"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full category-tag text-sm transition-all disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
              >
                {regeneratingBriefOg
                  ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Salvataggio…</>
                  : <><RefreshCw className="w-3.5 h-3.5" />Save Prompt & Regenerate OG</>}
              </button>
            </div>

            {/* Pending auto-publish banner */}            {brief?.pending_publish_at && !brief?.is_published && (() => {
              const publishAt = new Date(brief.pending_publish_at);
              const now = new Date();
              const hoursLeft = Math.max(0, Math.round((publishAt - now) / 3600000));
              const timeStr = publishAt.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
              return (
                <div data-testid="brief-pending-banner" className={`rounded-2xl p-4 flex items-start gap-3 ${dm ? "bg-amber-400/8 border border-amber-400/20" : "bg-amber-50 border border-amber-200"}`}>
                  <Clock className={`w-4 h-4 mt-0.5 flex-shrink-0 ${dm ? "text-amber-400" : "text-amber-600"}`} />
                  <div>
                    <p className={`text-sm font-semibold ${dm ? "text-amber-300" : "text-amber-800"}`}>Auto-publishes in ~{hoursLeft}h · {timeStr}</p>
                    <p className={`category-tag mt-0.5 ${dm ? "text-amber-400/70" : "text-amber-700"}`}>Edit and save the brief above to publish your revised version.</p>
                  </div>
                </div>
              );
            })()}

            {/* ── Social Post Text Preview ───────────────────────────────── */}
            <div className={`rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <div className={`px-6 py-4 border-b flex items-center justify-between ${dm ? "border-white/6 bg-[#161d1e]" : "border-black/5 bg-[#fafbfb]"}`}>
                <div className="flex items-center gap-2.5">
                  <Share2 className={`w-3.5 h-3.5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                  <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Social Post Text</p>
                  <span className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-white/6 text-slate-400" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>saved with brief</span>
                </div>
                <button
                  onClick={resetSocialTexts}
                  title="Re-generate from brief content"
                  className={`flex items-center gap-1 category-tag text-xs px-2.5 py-1 rounded-full transition-all ${dm ? "hover:bg-white/8 text-slate-400" : "hover:bg-[#f3f4f5] text-[#6b7a7d]"}`}
                >
                  <RefreshCw className="w-3 h-3" /> Regenerate
                </button>
              </div>
              <div className="p-6 space-y-5">
                {/* LinkedIn */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Linkedin className="w-3.5 h-3.5" style={{ color: "#0077B5" }} />
                      <label className={`category-tag text-xs font-medium ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>LinkedIn</label>
                    </div>
                    <button onClick={() => copyText(getSocialText("linkedin"))} data-testid="copy-linkedin-btn"
                      className={`flex items-center gap-1 category-tag text-xs px-2.5 py-1 rounded-full transition-all ${dm ? "hover:bg-white/8 text-slate-400" : "hover:bg-[#f3f4f5] text-[#6b7a7d]"}`}>
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <textarea
                    value={getSocialText("linkedin")}
                    onChange={e => setSocialText("linkedin", e.target.value)}
                    rows={5}
                    data-testid="social-text-linkedin"
                    className={`w-full resize-y rounded-xl px-4 py-3 text-sm leading-relaxed border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder-[#bac9cc]"}`}
                  />
                </div>

                {/* Twitter / X */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Twitter className="w-3.5 h-3.5" />
                      <label className={`category-tag text-xs font-medium ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>Twitter / X</label>
                      <span className={`category-tag text-xs ${getSocialText("twitter").length > 260 ? "text-amber-500" : dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                        {getSocialText("twitter").length}/280
                      </span>
                    </div>
                    <button onClick={() => copyText(getSocialText("twitter"))} data-testid="copy-twitter-btn"
                      className={`flex items-center gap-1 category-tag text-xs px-2.5 py-1 rounded-full transition-all ${dm ? "hover:bg-white/8 text-slate-400" : "hover:bg-[#f3f4f5] text-[#6b7a7d]"}`}>
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <textarea
                    value={getSocialText("twitter")}
                    onChange={e => setSocialText("twitter", e.target.value.slice(0, 280))}
                    rows={2}
                    maxLength={280}
                    data-testid="social-text-twitter"
                    className={`w-full resize-none rounded-xl px-4 py-3 text-sm leading-relaxed border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder-[#bac9cc]"} ${getSocialText("twitter").length > 260 ? "ring-2 ring-amber-400/40" : ""}`}
                  />
                </div>

                {/* Facebook */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Facebook className="w-3.5 h-3.5" style={{ color: "#1877F2" }} />
                      <label className={`category-tag text-xs font-medium ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>Facebook</label>
                    </div>
                    <button onClick={() => copyText(getSocialText("facebook"))} data-testid="copy-facebook-btn"
                      className={`flex items-center gap-1 category-tag text-xs px-2.5 py-1 rounded-full transition-all ${dm ? "hover:bg-white/8 text-slate-400" : "hover:bg-[#f3f4f5] text-[#6b7a7d]"}`}>
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <textarea
                    value={getSocialText("facebook")}
                    onChange={e => setSocialText("facebook", e.target.value)}
                    rows={4}
                    data-testid="social-text-facebook"
                    className={`w-full resize-y rounded-xl px-4 py-3 text-sm leading-relaxed border-0 outline-none focus:ring-2 focus:ring-[#006874]/30 transition-all ${dm ? "bg-[#252e2f] text-slate-200 placeholder-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder-[#bac9cc]"}`}
                  />
                </div>

                <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                  Edit and save the brief to persist custom post text. &quot;Regenerate&quot; resets to auto-generated text.
                </p>
              </div>
            </div>

            {/* Auto-Refresh Settings */}
            <div className={`rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <div className={`px-6 py-4 border-b flex items-center justify-between ${dm ? "border-white/6 bg-[#161d1e]" : "border-black/5 bg-[#fafbfb]"}`}>
                <div className="flex items-center gap-2.5">
                  <Clock className={`w-3.5 h-3.5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                  <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Auto-Refresh</p>
                </div>
                <Switch checked={autoSettings.auto_refresh_enabled} onCheckedChange={v => setAutoSettings(s => ({ ...s, auto_refresh_enabled: v }))} data-testid="auto-brief-toggle" className="data-[state=checked]:bg-[#006874]" />
              </div>
              <div className={`p-6 space-y-4 ${!autoSettings.auto_refresh_enabled ? "opacity-50 pointer-events-none" : ""}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={`category-tag block mb-2 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Generate on</Label>
                    <select value={autoSettings.day_of_week} onChange={e => setAutoSettings(s => ({ ...s, day_of_week: Number(e.target.value) }))}
                      data-testid="auto-brief-day"
                      className={`w-full rounded-xl px-3 py-2 text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`}>
                      {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                        <option key={d} value={i}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className={`category-tag block mb-2 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>At (UTC hour 0–23)</Label>
                    <Input type="number" min={0} max={23} value={autoSettings.hour}
                      onChange={e => setAutoSettings(s => ({ ...s, hour: Number(e.target.value) }))}
                      data-testid="auto-brief-hour"
                      className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={`category-tag block mb-2 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Send preview to</Label>
                    <Input type="email" value={autoSettings.notify_email}
                      onChange={e => setAutoSettings(s => ({ ...s, notify_email: e.target.value }))}
                      placeholder="mirko@tourismintel.ai" data-testid="auto-brief-email"
                      className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
                  </div>
                  <div>
                    <Label className={`category-tag block mb-2 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Review window (hours)</Label>
                    <Input type="number" min={1} max={72} value={autoSettings.review_window_hours}
                      onChange={e => setAutoSettings(s => ({ ...s, review_window_hours: Number(e.target.value) }))}
                      data-testid="auto-brief-window"
                      className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
                  </div>
                </div>
                <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                  Every {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][autoSettings.day_of_week]} at {String(autoSettings.hour).padStart(2, "0")}:00 UTC — AI generates the brief, sends you a preview email, and auto-publishes {autoSettings.review_window_hours}h later if you haven&apos;t already published it.
                </p>
              </div>
              <div className={`px-6 py-4 border-t flex justify-end ${dm ? "border-white/6" : "border-black/5"}`}>
                <button onClick={saveAutoSettings} disabled={savingAutoSettings} data-testid="save-auto-settings-btn"
                  className={`flex items-center gap-2 px-5 py-2 rounded-full category-tag transition-all disabled:opacity-50 ${dm ? "bg-[#252e2f] text-slate-200 hover:bg-[#303b3c]" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
                  {savingAutoSettings ? <><RefreshCw className="w-3 h-3 animate-spin" />Saving…</> : <><Check className="w-3 h-3" />Save Settings</>}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="mt-6">
          <RouterLink to="/admin?tab=events" data-testid="brief-events-tab-link" className={`flex items-center justify-between gap-3 rounded-2xl border-2 px-6 py-4 transition-colors ${dm ? "border-[#2a3435] bg-[#1a2122] hover:bg-[#1f2829]" : "border-[#e8ecee] bg-white hover:bg-[#f8fafa]"}`}>
            <div>
              <p className={`editorial-heading text-base font-medium flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}><CalendarDays className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} /> Industry events</p>
              <p className={`category-tag mt-0.5 text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Public catalogue, proposals queue, verification and AI Event Radar now live in Content → Events.</p>
            </div>
            <span className={`category-tag text-xs whitespace-nowrap ${dm ? "text-cyan-300" : "text-[#006874]"}`}>Open →</span>
          </RouterLink>
        </div>
      </div>
    </TabsContent>
  );
}
